import { AxiosResponse } from "axios";
import { parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import PlannerRepository from "@/repositories/PlannerRepository";
import {
  ProcessReport,
  CycleTimesReport,
  WorkingHourCurveReport,
  LastSeenProcessResult,
  Process,
  TagGroupedProcessResponse,
} from "@/types/Process";
import { apiClient } from "./clients";

const parseUtcDate = (dateText: string): Date => utcToZonedTime(parseISO(dateText), "UTC");

const mapProcess = (process: Process<string>) => ({
  ...process,
  start_time: parseUtcDate(process.start_time),
  end_time: parseUtcDate(process.end_time),
});

const loadTagGroupedProcesses = (customerName: string, siteId: string) =>
  apiClient
    .get<TagGroupedProcessResponse>(`/ops-process/group-tag/${customerName}/${siteId}`)
    .then((response) => {
      const mappedProcesses = response.data.data.map((process) => ({
        ...process,
        processes: process.processes.map((process) => {
          const mappedProcess = {
            ...process,
            start_time: parseUtcDate(process.start_time as unknown as string),
            end_time: parseUtcDate(process.end_time as unknown as string),
            work_intervals: (process.work_intervals || []).map((workInterval) => ({
              ...workInterval,
              start_time: parseUtcDate(workInterval.start_time as unknown as string),
              end_time: parseUtcDate(workInterval.end_time as unknown as string),
            })),
          };
          return {
            ...mappedProcess,
            breaks: PlannerRepository.calculateProcessBreaks(mappedProcess),
          };
        }),
      }));

      return {
        levelOrders: response.data.levelOrders,
        data: mappedProcesses,
      };
    });

const loadProcessVideoUrl = (
  customerName: string,
  siteId: string,
  processDbId: string,
): Promise<{ url: string }> =>
  apiClient
    .get<void, AxiosResponse<{ url: string }>>(
      `/ops-process/process-video/${customerName}/${siteId}/${processDbId}`,
    )
    .then((response) => response.data);

const loadProcessReports = (customerName: string, siteId: string) =>
  apiClient
    .get<ProcessReport[]>(`/ops-process/report/${customerName}/${siteId}`)
    .then((response) => response.data);

const createProcessReport = (
  customerName: string,
  siteId: string,
  payload: Partial<ProcessReport>,
) =>
  apiClient
    .post<ProcessReport>(`/ops-process/report/${customerName}/${siteId}`, payload)
    .then((response) => response.data);

const updateProcessReport = (
  customerName: string,
  siteId: string,
  id: string,
  payload: Partial<ProcessReport>,
) =>
  apiClient
    .put<ProcessReport>(`/ops-process/report/${customerName}/${siteId}/${id}`, payload)
    .then((response) => response.data);

const deleteProcessReport = (customerName: string, siteId: string, id: string) =>
  apiClient
    .delete<void>(`/ops-process/report/${customerName}/${siteId}/${id}`)
    .then((response) => response.data);

type ReportSummaryResponse<T> = T extends "cycle_times" ? CycleTimesReport : WorkingHourCurveReport;

const loadReportProcessSummary = <T extends ProcessReport["plot"]["type"]>(
  customerName: string,
  siteId: string,
  plotType: T,
  query?: Partial<{ processes: number[]; building: string[]; level: string[]; section: string[] }>,
) =>
  apiClient
    .get<ReportSummaryResponse<T>>(`/ops-process/report/${plotType}/${customerName}/${siteId}`, {
      params: query,
    })
    .then((response) => {
      if (plotType === "cycle_times") {
        const data = response.data as CycleTimesReport;

        return data.map((item) => ({
          ...item,
          processes: item.processes.map((process) => ({
            ...process,
            start_time: parseUtcDate(process.start_time as unknown as string),
            end_time: parseUtcDate(process.end_time as unknown as string),
            work_intervals: process.work_intervals.map((workInterval) => ({
              ...workInterval,
              start_time: parseUtcDate(workInterval.start_time as unknown as string),
              end_time: parseUtcDate(workInterval.end_time as unknown as string),
            })),
          })),
          planned_event: {
            ...item.planned_event,
            start: parseUtcDate(item.planned_event.start as unknown as string),
            end: parseUtcDate(item.planned_event.end as unknown as string),
          },
        }));
      }

      return response.data;
    });

const loadLastSeenProcess = (
  customerName: string,
  siteId: string,
): Promise<LastSeenProcessResult> =>
  apiClient
    .get<void, AxiosResponse<LastSeenProcessResult<string>>>(
      `/ops-process/last-seen-process/${customerName}/${siteId}`,
    )
    .then((response) => {
      return {
        ...response.data,
        process: response.data.process && mapProcess(response.data.process),
      };
    });

const loadNextLastSeenProcess = (
  customerName: string,
  siteId: string,
  processId: string,
): Promise<LastSeenProcessResult> =>
  apiClient
    .get<void, AxiosResponse<LastSeenProcessResult<string>>>(
      `/ops-process/last-seen-process/${customerName}/${siteId}/${processId}/next`,
    )
    .then((response) => {
      return {
        ...response.data,
        process: response.data.process && mapProcess(response.data.process),
      };
    });

export default {
  loadTagGroupedProcesses,
  loadProcessVideoUrl,
  loadProcessReports,
  createProcessReport,
  updateProcessReport,
  deleteProcessReport,
  loadReportProcessSummary,
  loadLastSeenProcess,
  loadNextLastSeenProcess,
};
